import { ViewLayoutPlacement } from "@faire/web-api/indigofair/data/ViewLayoutPlacement";
import { markPageJsExecuted } from "@faire/web/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { UPLTHookOptions } from "@faire/web/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";

import { useSettingSDUICategoryPageRedesign } from "@faire/retailer/settings/useSettingSDUICategoryPageRedesign";
import { useIsBrowseFpsLoadedAndHasNoResults } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/filterablePaginatedSection/useIsBrowseFPSLoadedAndHasNoResults";
import { useBrowseLayoutDefinedFilteredComponentsQuery } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useBrowseLayoutDefinedFilteredComponentsQuery/useBrowseLayoutDefinedFilteredComponentsQuery";
import { useBrowseLayoutQuery } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useBrowseLayoutQuery/useBrowseLayoutQuery";
import { useIsBrowseLayoutLoadedAndHasNoResults } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useBrowseLayoutQuery/useIsBrowseLayoutLoadedAndHasNoResults";
import { useIsBrowseLayoutLoading } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useBrowseLayoutQuery/useIsBrowseLayoutLoading";
import { useCategoryPageQueryKeys } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useCategoryPageQueryKeys";
import { useBrowseV3LayoutQuery } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/v3/useBrowseV3LayoutQuery";
import { useIsBrowseV3FpsLayoutLoadedAndHasNoResults } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/v3/useIsBrowseV3FpsLayoutLoadedAndHasNoResults";
import { useCategoryPageActionName } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/useCategoryPageActionName";

const useIgnoreImages = () => {
  const isBrowseV3LayoutApiEnabled = useSettingSDUICategoryPageRedesign();

  const isBrowseLayoutLoadedAndHasNoResults =
    useIsBrowseLayoutLoadedAndHasNoResults();
  const isBrowseLayoutError = useBrowseLayoutQuery().isError;

  const isBrowseV3FpsLayoutLoadedAndHasNoResults =
    useIsBrowseV3FpsLayoutLoadedAndHasNoResults();
  const isBrowseV3FpsLayoutError = useBrowseV3LayoutQuery(
    ViewLayoutPlacement.FPS
  ).isError;

  const { isError: isFpsError } =
    useBrowseLayoutDefinedFilteredComponentsQuery();
  const isFpsLoadedAndHasNoResults = useIsBrowseFpsLoadedAndHasNoResults();

  return isBrowseV3LayoutApiEnabled
    ? isBrowseV3FpsLayoutLoadedAndHasNoResults ||
        isBrowseV3FpsLayoutError ||
        isFpsError ||
        isFpsLoadedAndHasNoResults
    : isBrowseLayoutLoadedAndHasNoResults || isBrowseLayoutError;
};

const usePageLoadKey = (): string => {
  const queryKey = useCategoryPageQueryKeys();

  return JSON.stringify(queryKey);
};

const pageJsExecutedMark = markPageJsExecuted();

export const useSDUICategoryPageUPLT = (): Omit<
  UPLTHookOptions,
  "numComponents"
> => {
  const isBrowseV3LayoutApiEnabled = useSettingSDUICategoryPageRedesign();

  const { isError: isBrowseLayoutError, dataUpdatedAt: browseLayoutUpdatedAt } =
    useBrowseLayoutQuery();
  const pageName = useCategoryPageActionName();

  const isBrowseLayoutLoading = useIsBrowseLayoutLoading();
  const ignoreImages = useIgnoreImages();
  const pageLoadKey = usePageLoadKey();

  const {
    isError: isBrowseV3FpsLayoutError,
    isFetching: isBrowseV3FpsLayoutFetching,
    data: browseV3FpsLayoutData,
    dataUpdatedAt: browseV3FpsLayoutUpdatedAt,
  } = useBrowseV3LayoutQuery(ViewLayoutPlacement.FPS);
  const isBrowseV3FpsLayoutLoading =
    isBrowseV3FpsLayoutFetching || browseV3FpsLayoutData === undefined;

  const {
    data: filteredComponentsData,
    isError: isFilteredComponentsError,
    isFetching: isFilteredComponentsFetching,
    dataUpdatedAt: filteredComponentsDataUpdatedAt,
  } = useBrowseLayoutDefinedFilteredComponentsQuery();

  const isFilteredComponentsLoading =
    isFilteredComponentsFetching || filteredComponentsData === undefined;

  const isLayoutLoading = isBrowseV3LayoutApiEnabled
    ? isBrowseV3FpsLayoutLoading && !isBrowseV3FpsLayoutError
    : isBrowseLayoutLoading && !isBrowseLayoutError;

  const isPageLoading =
    isLayoutLoading ||
    (isFilteredComponentsLoading && !isFilteredComponentsError);

  const dataUpdatedAt = Math.max(
    filteredComponentsDataUpdatedAt,
    isBrowseV3LayoutApiEnabled
      ? browseV3FpsLayoutUpdatedAt
      : browseLayoutUpdatedAt
  );

  return {
    name: pageName,
    isPageLoading,
    ignoreImages,
    enableTrackPerformanceBreakdown: "internal-tracking",
    pageLoadKey,
    dataUpdatedAt,
    pageJsExecutedMark: pageJsExecutedMark,
  };
};
